import { nui } from 'lib/nodes-ui';
import { checkLinkRectIntersection, rectIsFullyWithinRect } from 'lib/geom';
import { scaleRect } from 'lib/util';
class DragDrop {

  constructor() {
    this.targetRects = {};
    this.prevHitTopParentId = null;
    this.newHome = {
      parentId: null,
      first: false,
      last: false,
      beforeId: null,
      afterId: null,
    };
    this.highlightedTag = null;
    this.lastDropzone = {};
    this.showingDropzone = false;
    this.viewportRect = {
      top: 0,
      bottom: window.innerHeight - 5,
      left: 0,
      right: window.innerWidth -5,
    };
    this.mouseCoords = {
      x: 0,
      y: 0,
    };
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  updateViewportRect() {
    this.viewportRect = {
      top: 0,
      bottom: window.innerHeight -5,
      left: 0,
      right: window.innerWidth -5,
    };
  }

  setMouseCoords({ x, y }) {
    this.mouseCoords.x = x;
    this.mouseCoords.y = y;
  }

  calculateTargetRects(targetIds=Object.keys(this.canvas.nodeTags)) {
    // todo: make buffer a default
    this.updateViewportRect();
    for (const targetId of targetIds) {

      const nodeTag = this.canvas.nodeTags[targetId];
      let target;
      if (this.isShowingPopout(nodeTag.instance)) {
        /* use pop out container */
        target = nodeTag.childNodesTag.refs.screen;
      } else {
        target = nodeTag.refs.screen;
      }

      if (target) {
        let targetRect;
        if (nodeTag.instance.getParent() && nodeTag.instance.getParent().getLayoutName() !== 'layout-freehand') {
          targetRect = this.getTargetRect({target});
        } else if (this.isShowingPopout(nodeTag.instance)) {
          targetRect = this.getTargetRect({target});
        } else {
          targetRect = this.getTargetRect({target,buffer: 8});
        }
        this.targetRects[targetId] = targetRect;
      } else {
        delete this.targetRects[targetId];
      }
    }
  }

  getTargetRect({target,buffer}) {
    let targetRect = target.getBoundingClientRect();
    return this.reduceRectByBuffer({ targetRect, buffer });
  }

  reduceRectByBuffer({targetRect,buffer}) {
    if (1 || !buffer) return targetRect;

    const bufferedRect = Object.assign({},this.rect2Obj(targetRect));

    bufferedRect.left += buffer;
    bufferedRect.right -= buffer;

    if (buffer) {
      if (bufferedRect.height >= 50) {
        bufferedRect.top += buffer;
        bufferedRect.bottom -= buffer;
      } else {
        bufferedRect.top += 5;
        bufferedRect.bottom -= 5;
      }
    }
    bufferedRect.x = bufferedRect.left;
    bufferedRect.y = bufferedRect.top;
    bufferedRect.width = bufferedRect.right - bufferedRect.left;
    bufferedRect.height = bufferedRect.bottom - bufferedRect.top;
    return bufferedRect;
  }

  rect2Obj(rect) {
    return {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
      x: rect.x,
      y: rect.y
    };
  }

  hit(dragging, target) {
    return !(dragging.right < target.left || dragging.left > target.right ||
            dragging.bottom < target.top || dragging.top > target.bottom);
  }

  isContainedIn({dragging, target, spillover = 0}) {
    return (dragging.right > target.right - spillover && dragging.left < target.left + spillover &&
            dragging.bottom > target.bottom - spillover && dragging.top < target.top + spillover);
  }

  isLeft(dragging, target) {
    return (dragging.left < target.left);
  }

  isRight(dragging, target) {
    return (dragging.left > (target.right - (target.width / 2)));
  }

  isUp(dragging, target) {
    return (dragging.top < target.top);
  }

  isDown(dragging, target) {
    return (dragging.top > (target.bottom - (target.height / 2)));
  }

  topIsCloserThanBottom(dragging, target) {
    return Math.abs(target.top - dragging.top) < Math.abs(target.bottom - dragging.top);
  }

  leftIsCloserThanRight(dragging, target) {
    return Math.abs(target.left - dragging.left) < Math.abs(target.right - dragging.left);
  }

  isClose(dragging, target) {
    const threshold = 30;
    const margin = nui.gutterSize;

    const inColumn = (dragging, target) => {
      return (dragging.left >= target.left && dragging.left <= target.right) || (dragging.right >= target.left && dragging.right <= target.right) || (dragging.left <= target.left && dragging.right >= target.right);
    };

    const inRow = (dragging, target) => {
      return (dragging.top >= target.top && dragging.top <= target.bottom) || (dragging.bottom >= target.top && dragging.bottom <= target.bottom) || (dragging.top <= target.top && dragging.bottom >= target.bottom);
    };

    let snapSide;

    if (inRow(dragging, target)) {
      if (Math.abs(dragging.right - target.left) < threshold) {
        snapSide = 'left';
      }
      if (Math.abs(dragging.left - target.right) < threshold) {
        snapSide = 'right';
      }
    }

    if (inColumn(dragging, target)) {
      if (Math.abs(dragging.top - target.bottom) < threshold) {
        snapSide = 'bottom';
      }
      if (Math.abs(dragging.bottom - target.top) < threshold) {
        snapSide = 'top';
      }
    }

    if (snapSide && snapSide.match(/top|bottom/)) {
      const leftDist = Math.abs(dragging.left - target.left);
      const rightDist = Math.abs(dragging.right - target.right);

      if (leftDist <= rightDist) {
        if (leftDist < margin * 2) {
          snapSide += '--left';
        }
      } else {
        if (rightDist < margin * 2) {
          snapSide += '--right';
        }
      }
    } else if (snapSide && snapSide.match(/left|right/)) {
      const topDist = Math.abs(dragging.top - target.top);
      const bottomDist = Math.abs(dragging.bottom - target.bottom);

      if (topDist <= bottomDist) {
        if (topDist < margin * 2) {
          snapSide += '--top';
        }
      } else {
        if (bottomDist < margin * 2) {
          snapSide += '--bottom';
        }
      }
    }

    return snapSide;

  }

  getEndPointHoveredNode(link) {

    const nodes = store.getNodesArray();
    const margin = 10;

    const fromLinkRect = {
      top: link.fromEndPoint.y - margin,
      bottom: link.fromEndPoint.y + margin,
      left: link.fromEndPoint.x - margin,
      right: link.fromEndPoint.x + margin,
    };

    const toLinkRect = {
      top: link.toEndPoint.y - margin,
      bottom: link.toEndPoint.y + margin,
      left: link.toEndPoint.x - margin,
      right: link.toEndPoint.x + margin,
    };

    const hits = {};

    for (const n of nodes) {

      const nodeRect = {
        top: n.absolutePosition.y,
        bottom: n.absolutePosition.y + n.height,
        left: n.absolutePosition.x,
        right: n.absolutePosition.x + n.width
      };

      if (link.fromNodeId == 0 && this.hit(nodeRect, fromLinkRect)) {
        hits.from = { direction: 'from', endPoint: link.fromEndPoint, node: n };
      }
      if (link.toNodeId == 0 && this.hit(nodeRect, toLinkRect)) {
        hits.to = { direction: 'to', endPoint: link.toEndPoint, node: n };
      }
    }

    return hits;
  }

  getHoveredLinkEndPoint(nodeRect) {
    const allLinks = this.canvas.store.getLinks();

    const margin = 10;
    for (const link of allLinks) {
      const fromLinkRect = {
        top: link.fromEndPoint.y - margin,
        bottom: link.fromEndPoint.y + margin,
        left: link.fromEndPoint.x - margin,
        right: link.fromEndPoint.x + margin,
      };
      const toLinkRect = {
        top: link.toEndPoint.y - margin,
        bottom: link.toEndPoint.y + margin,
        left: link.toEndPoint.x - margin,
        right: link.toEndPoint.x + margin,
      };

      const scaledRect = scaleRect({ rect: nodeRect, canvas: this.canvas });

      if (link.fromEndPoint.nodeId == 0 && this.hit(scaledRect, fromLinkRect)) {
        return {link, endPoint: link.fromEndPoint};
      } else if (link.toEndPoint.nodeId == 0 && this.hit(scaledRect, toLinkRect)) {
        return {link, endPoint: link.toEndPoint};
      }
    }
  }

  getHoveredLinks({ draggingNodeId, draggingRect, buffer }) {

    const matchedTargets = [];

    const linkTags = this.canvas.store.getLinks()
     .filter(l => this.canvas.linkTags[l.id] && l.showArc())
     .map(l => this.canvas.linkTags[l.id]);

    for (const linkTag of linkTags) {
      const { fromNodeId, toNodeId, fromPoint, toPoint, thirdPoint } = linkTag;
      if (!fromPoint || !toPoint || !thirdPoint) {
        continue;
      }
      const bufferedRect = this.reduceRectByBuffer({ targetRect: draggingRect, buffer: buffer });
      const scaledRect = scaleRect({ rect: bufferedRect, canvas: this.canvas });

      if (checkLinkRectIntersection({ link: linkTag.instance, linkTag, rect: scaledRect })) {
        matchedTargets.push(linkTag);
      }
    }

    return matchedTargets;
  }

  getHoveredNodeAndPlacement({ draggingNodeId, draggingRect, buffer }) {
    const matchedTargets = [];
    const targets = this.canvas.nodeTags;
    let targetIds = Object.keys(this.canvas.nodeTags);

    const draggingNode = this.canvas.store.nodes.get(draggingNodeId);
    if (!draggingNode) {
      return;
    }

    if (draggingNode.getGoverningLayoutName() == 'layout-freehand') {
      targetIds = draggingNode.getParent().descendants().map(n => n.id);
    }

    const dragDescendantIds = draggingNode.descendants().map(n => n.id);

    //don't even try to add ourselves to a link if we already are an rNode;
    if (!draggingNode.link()) {
      const linkTags = this.canvas.store.getLinks()
       .filter(l => !l.linkNodeId)
       .filter(l => this.canvas.linkTags[l.id] && l.showArc())
       .map(l => this.canvas.linkTags[l.id]);

      for (const linkTag of linkTags) {
        // Don't adopt if its not a valid link node
        if (!linkTag.instance.isValidLinkNodeId(draggingNode.id)) continue;

        const { fromNodeId, toNodeId, fromPoint, toPoint, thirdPoint } = linkTag;
        if (!fromPoint || !toPoint || !thirdPoint) {
          continue;
        }
        //Don't adopt if there already is a linkNode.
        if (linkTag.instance.linkNode()) continue;

        //Don't adopt your own endpoint
        if (draggingNode.id === fromNodeId || draggingNode.id === toNodeId) continue;
        const bufferedRect = this.reduceRectByBuffer({ targetRect: draggingRect, buffer: buffer });
        const scaledRect = scaleRect({ rect: bufferedRect, canvas: this.canvas });

        if (checkLinkRectIntersection({ link: linkTag.instance, linkTag, rect: scaledRect })) {
          matchedTargets.push({ linkTag });
        }
      }
    }

    for (const targetId of targetIds) {
      if (targetId == draggingNodeId) continue;
      if (this.canvas.getSelectedElementIds().includes(targetId)) continue;
      if (!this.canvas.store.nodes.get(targetId)) continue;
      if (dragDescendantIds.length && dragDescendantIds.includes(targetId)) {
        continue;
      }


      const storeNode = this.canvas.store.getNode(targetId);
      if (nui.dontAllowDropInTarget(draggingNode, storeNode)) continue;

      const target = targets[targetId].refs.screen;
      if (!target) continue;

      // TODO: re-enable cache and recalc target rect if it used to have
      // a dropzone and no longer does
      if (1 || !this.targetRects[targetId]) {
        const targetRect = this.getTargetRect({target,buffer});
        this.targetRects[targetId] = targetRect;
      }
      if (!this.hit(this.targetRects[targetId], this.viewportRect)) {
        continue;
      }

      if (!storeNode.parentId) {
        const targetRect = this.reduceRectByBuffer({ targetRect: this.targetRects[targetId], buffer: buffer });
        const snapSide = this.isClose(draggingRect, targetRect);

        if (snapSide) {
          matchedTargets.push({ nodeTag: targets[targetId], placement: 'snap-' + snapSide });
        }
      }

      function doInPlacement(layout, draggingRect, targetId) {
        if (layout == 'layout-row') {
          if (this.topIsCloserThanBottom(draggingRect, this.targetRects[targetId])) {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'first-child' });
          } else {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'last-child' });
          }
        } else {
          if (this.leftIsCloserThanRight(draggingRect, this.targetRects[targetId])) {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'first-child' });
          } else {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'last-child' });
          }
        }
      }

      if (this.hit(draggingRect, this.targetRects[targetId])) {

        const layout = storeNode.getGoverningLayoutName() || storeNode.getLayoutName();

        if (storeNode.isTopLevel() || storeNode.getGoverningLayoutName() == 'layout-freehand') {
          doInPlacement.call(this, layout, draggingRect, targetId);
        } else if (layout == 'layout-row') {
          if (this.isUp(draggingRect, this.targetRects[targetId])) {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'before' });
          } else if (this.isDown(draggingRect, this.targetRects[targetId])) {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'after' });
          } else {
            doInPlacement.call(this, storeNode.getLayoutName(), draggingRect, targetId);
          }
        } else {
          if (this.isLeft(draggingRect, this.targetRects[targetId])) {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'before' });
          } else if (this.isRight(draggingRect, this.targetRects[targetId])) {
            matchedTargets.push({ nodeTag: targets[targetId], placement: 'after' });
          } else {
            doInPlacement.call(this, storeNode.getLayoutName(), draggingRect, targetId);
          }
        }

      }

    }

    const matchedNodeTargets = matchedTargets.filter(t => t.nodeTag);
    const matchedLinkTargets = matchedTargets.filter(t => t.linkTag);
    matchedNodeTargets.sort((a,b) => this.mouseDistanceToRect(this.targetRects[a.nodeTag.id]) - this.mouseDistanceToRect(this.targetRects[b.nodeTag.id]));
    const insideFreehandWithOneLinkCollision = () => {
      if (matchedLinkTargets.length === 1 && matchedNodeTargets.length > 0) {
        const insideFreehand = matchedNodeTargets.every(nt => {
          const node = this.canvas.store.getNode(nt.nodeTag.id);
          const isFreehand = node.getLayoutName() === 'layout-freehand';
          return isFreehand && rectIsFullyWithinRect(draggingRect, this.targetRects[nt.nodeTag.id]);
        });
        return insideFreehand;
      } else {
        return false;
      }
    };

    if (matchedTargets.length === 0) {
      return false;
    } else if (matchedTargets.length === 1) {
      return matchedTargets[0];
    } else if (matchedNodeTargets.length === 0) {
      //if we have multiple link hits and no node hits just take the first one
      return matchedTargets[0];
    } else if (insideFreehandWithOneLinkCollision()) {
      return matchedLinkTargets[0];
    } else {
      //never use a link as the match if we also have a hit on a node
      const sharedParentTargets = matchedNodeTargets.filter(t => {
        return t.nodeTag.topLevelParentTag().id === this.prevHitTopParentId;
      });
      let matchedTarget;

      let newHitTopParentId;
      if (matchedNodeTargets && matchedNodeTargets[0] && matchedNodeTargets[0].nodeTag) {
        newHitTopParentId = matchedNodeTargets[0].nodeTag.topLevelParentTag().id;
      }
      if (sharedParentTargets.length !== 0 && this.prevHitTopParentId && this.prevHitTopParentId === newHitTopParentId) {
        matchedTarget = this.chooseMatchedTarget({ matchedTargets: sharedParentTargets, draggingRect, gravity: 'heritage' });
      } else {
        matchedTarget = this.chooseMatchedTarget({ matchedTargets: matchedNodeTargets, draggingRect, gravity: 'mouse' });
      }

      const topParent = this.canvas.store.getNode(matchedTarget.nodeTag.id).getTopLevelParent();
      if (topParent.children().length !== 0 && !topParent.getIsCollapsed()) {
        this.prevHitTopParentId = matchedTarget.nodeTag.topLevelParentTag().id;
      } else {
        this.prevHitTopParentId = false;
      }
      return matchedTarget;
    }

  }

  rectCenter(rect) {
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  }

  mouseDistanceToRect(rect) {
    const mx = this.mouseCoords.x;
    const my = this.mouseCoords.y;
    const rectCenter = this.rectCenter(rect);

    const dx = Math.max(Math.abs(mx - rectCenter.x) - rect.width / 2, 0);
    const dy = Math.max(Math.abs(my - rectCenter.y) - rect.height / 2, 0);
    return dx * dx + dy * dy;
  };

  distanceBetweenRectOrigins(dragging, target) {
    return Math.sqrt(
      Math.pow(dragging.top - target.top, 2) +
      Math.pow(dragging.left - target.left, 2)
    );
  };

  chooseMatchedTarget({ matchedTargets, draggingRect, gravity }) {
    const beforeTargets = matchedTargets.filter(t => t.placement == 'before').sort((a, b) => {
      const aNode = this.canvas.store.getNode(a.nodeTag.id);
      const bNode = this.canvas.store.getNode(b.nodeTag.id);
      return (aNode.getLineage().length - bNode.getLineage().length);
    });

    // filter matched targets based on lineage of before targets
    if (beforeTargets.length) {
      beforeTargets.forEach(bt => {
        matchedTargets = matchedTargets.filter(mt => {
          const mtNode = this.canvas.store.getNode(mt.nodeTag.id);
          if (mtNode.getLineage().map(l => l.id).includes(bt.nodeTag.id)) {
            return mtNode.id === bt.nodeTag.id;
          }
          return true;
        });
      });
    }

    const matchedTargetIds = matchedTargets.map( target => target.nodeTag.id );
    let closestTargetId = matchedTargetIds.shift();
    let relevantEdge;
    let oppositeEdge;

    let matchedTarget = matchedTargets.find(t => t.nodeTag.id == closestTargetId);

    let closestDistance;
    switch(gravity) {
      case 'mouse':
        closestDistance = this.mouseDistanceToRect(this.targetRects[closestTargetId]);
        break;
      case 'heritage':
        closestDistance = this.distanceBetweenRectOrigins(draggingRect, this.targetRects[closestTargetId]);
        break;
      default:
        console.log('unknown gravity: ', gravity);
    }

    for (const id of matchedTargetIds) {

      matchedTarget = matchedTargets.find(t => t.nodeTag.id == id);

      let minDistance;
      switch(gravity) {
        case 'mouse':
          minDistance = this.mouseDistanceToRect(this.targetRects[id]);
          break;
        case 'heritage':
          minDistance = this.distanceBetweenRectOrigins(draggingRect, this.targetRects[id]);
          break;
        default:
          console.log('unknown gravity: ', gravity);
      }

      if (minDistance < closestDistance) {
        closestDistance = minDistance;
        closestTargetId = id;
      }
    }

    return matchedTargets.find(t => t.nodeTag.id == closestTargetId);
  }

  clearNewHome() {
    this.newHome = {
      parentId: null,
      first: false,
      last: false,
      beforeId: null,
      afterId: null,
    };
  }

  showLinkDropzone({ initialDragValues, dropzone, linkTag }) {
    this.lastDropzone.initialDragValues = initialDragValues;
    this.lastDropzone.dropzone = dropzone;
    this.lastDropzone.linkTag = linkTag;
    this.lastDropzone.targetTag = null;
    this.lastDropzone.collapsedDropzoneContainer = null;

    const { scale } = this.canvas.getMatrix();
    dropzone.setAttribute('data-placement', 'relationship');
    dropzone.setAttribute('data-childless', true);
    dropzone.classList.add('forced-on');
    this.linkDropzoneTipTimer = setTimeout(() => {
      dropzone.setAttribute('data-tip-align','true-right');
      dropzone.setAttribute('data-tip','Hold shift to prevent attachment to line');
    }, 1000);
    dropzone.style.width = ( initialDragValues.width / scale ) + 'px';
    dropzone.style.height = ( initialDragValues.height / scale ) + 'px';

    dropzone.style.display = 'block';
    dropzone.setAttribute('data-layout', 'relationship');
    if (!dropzone.classList.contains('link-dropzone')) {
      dropzone.classList.add('link-dropzone');
    }
    const {x, y} = nui.getLNodePointForAdoptedNode({ link: linkTag.instance, draggingRect: initialDragValues });

    dropzone.style.left = `${x}px`;
    dropzone.style.top = `${y}px`;
    this.canvas.nodesTag.refs.topNodes.insertAdjacentElement('afterbegin', dropzone);
    this.showingDropzone = true;
  }

  getSnappedPosition(snap, dragNodeTag) {

    const margin = 24;
    let x, y;

    // primary placement

    if (snap.placement.match(/snap-bottom/)) {
      y = snap.nodeTag.instance.y + snap.nodeTag.instance.height + margin;
    }
    if (snap.placement.match(/snap-top/)) {
      y = snap.nodeTag.instance.y - dragNodeTag.instance.height - margin;
    }
    if (snap.placement.match(/snap-right/)) {
      x = snap.nodeTag.instance.x + snap.nodeTag.instance.width + margin;
    }
    if (snap.placement.match(/snap-left/)) {
      x = snap.nodeTag.instance.x - dragNodeTag.instance.width - margin;
    }

    // secondary placement

    if (snap && snap.placement.match(/--left/)) {
      x = snap.nodeTag.instance.x;
    } else if (snap && snap.placement.match(/--right/)) {
      x = snap.nodeTag.instance.x + snap.nodeTag.instance.width - dragNodeTag.instance.width;
    } else if (snap && snap.placement.match(/--top/)) {
      y = snap.nodeTag.instance.y;
    } else if (snap && snap.placement.match(/--bottom/)) {
      y = snap.nodeTag.instance.y + snap.nodeTag.instance.height - dragNodeTag.instance.height;
    }

    return { x, y };
  }

  showDropzone({ initialDragValues, placement, dropzone, targetTag, dragNodeTag }) {
    this.lastDropzone.initialDragValues = initialDragValues;
    this.lastDropzone.placement = placement;
    this.lastDropzone.dropzone = dropzone;
    this.lastDropzone.targetTag = targetTag;
    this.lastDropzone.dragNodeTag = dragNodeTag;
    this.lastDropzone.linkTag = null;
    this.lastDropzone.collapsedDropzoneContainer = null;
    this.showingDropzone = true;
    this.clearHighlightedTag();

    if (placement.match(/snap-/) && dragNodeTag) {
      const snap = { placement: placement, nodeTag: targetTag };
      let { x, y } = this.getSnappedPosition(snap, dragNodeTag);
      if (!Number.isFinite(x)) x = dragNodeTag.instance.x;
      if (!Number.isFinite(y)) y = dragNodeTag.instance.y;
      dragNodeTag.refs.container.style.opacity = 0.5;
      dropzone.style.width = initialDragValues.width + 'px';
      dropzone.style.height = initialDragValues.height + 'px';
      dropzone.style.left = x + 'px';
      dropzone.style.top = y + 'px';
      dropzone.classList.add('dropzone--top-level');
      dropzone.style.display = 'block';
      dropzone.setAttribute('data-layout', 'snap');
      return;
    } else {
      dropzone.classList.contains('dropzone--top-level') && dropzone.classList.remove('dropzone--top-level');
    }

    let targetEl = document.querySelector(`[data-id="${targetTag.id}"]`);
    const targetNode = this.canvas.store.getNode(targetTag.id);

    if (targetNode.getLayoutName() === 'layout-freehand' || (targetNode.getIsCollapsed() && ['in', 'first-child', 'last-child'].includes(placement))) {
      this.hideDropzone(dropzone);
      this.highlightedTag = targetTag;

      if (this.isShowingPopout(targetNode)) {
        targetTag.childNodesTag.refs.container.classList.add('top-hover');
      } else {
        targetTag.refs.container.classList.add('top-hover');
        targetTag.refs.nodeTopLine.classList.add('node-top-line-top-hover');
      }
      if (targetNode.getIsCollapsed() || targetNode.getLayoutName() === 'layout-freehand') {
        if (this.isShowingPopout(targetNode)) {
          targetTag.childNodesTag.refs.container.classList.add('collapsed-dropzone');
          this.lastDropzone.collapsedDropzoneContainer = targetTag.childNodesTag.refs.container;
        } else {
          targetTag.refs.container.classList.add('collapsed-dropzone');
          this.lastDropzone.collapsedDropzoneContainer = targetTag.refs.container;
        }
      }
      this.newHome.parentId = targetNode.id;
      return;
    }

    dropzone.style.left = '';
    dropzone.style.top = '';
    if (dropzone.classList.contains('link-dropzone')) {
      dropzone.classList.remove('link-dropzone');
    }
    if (targetEl && targetNode) {
      // FIX margins and dimensions should respect the container
      dropzone.setAttribute('data-placement', placement);
      if (targetNode.children().length == 0) {
        dropzone.setAttribute('data-childless', true);
      } else {
        dropzone.setAttribute('data-childless', false);
      }
      dropzone.style.width = initialDragValues.width + 'px';
      dropzone.style.height = initialDragValues.height - 7 + 'px';
      dropzone.style.display = 'block';
      if (placement == 'first-child' || placement == 'in') {

        if (this.isShowingPopout(targetNode)) {
          targetEl = targetEl.querySelector('.col-group-container .child-nodes');
          targetEl.insertAdjacentElement('afterbegin', dropzone);
        } else {
          targetEl = targetEl.querySelector('.col-group-container');
          targetEl.insertAdjacentElement('afterbegin', dropzone);
        }
        this.newHome.parentId = targetNode.id;
        this.newHome.first = true;
        dropzone.setAttribute('data-layout', targetTag.getLayoutName());
      } else if (placement == 'last-child') {
        if (targetTag.instance.children().length) {
          targetEl = targetEl.querySelector('.child-nodes');
          targetEl.insertAdjacentElement('beforeend', dropzone);
        } else {
          targetEl = targetEl.querySelector('.col-group-container');
          targetEl.insertAdjacentElement('beforeend', dropzone);
        }
        this.newHome.parentId = targetNode.id;
        this.newHome.last = true;
        dropzone.setAttribute('data-layout', targetTag.getLayoutName());
      } else if (placement == 'before') {
        if (targetNode.getGoverningLayoutName() !== 'layout-freehand') {
          targetEl.parentNode.insertAdjacentElement('beforebegin', dropzone);
          const parentNode = targetNode.getParent();
          if (parentNode) {
            this.newHome.parentId = parentNode.id;
            this.newHome.beforeId = targetNode.id;
          }
          dropzone.setAttribute('data-layout', targetTag.getGoverningLayoutName());
        } else {
          this.hideDropzone(dropzone);
        }
      } else if (placement == 'after') {
        if (targetNode.getGoverningLayoutName() !== 'layout-freehand') {
          targetEl.parentNode.insertAdjacentElement('afterend', dropzone);
          const parentNode = targetNode.getParent();
          if (parentNode) {
            this.newHome.parentId = parentNode.id;
            this.newHome.afterId = targetNode.id;
          }
          dropzone.setAttribute('data-layout', targetTag.getGoverningLayoutName());
        } else {
          this.hideDropzone(dropzone);
        }
      }

      const tlpTag = targetTag.topLevelParentTag();
      tlpTag.rediscoverLinkPositions();
      if (tlpTag.linkTag()) {
        const box = tlpTag.linkTag().describeArc().outerBox;
        tlpTag.linkTag().setClipPolygon(box);
      }
    }
  }

  showLastDropzone() {
    const initialDragValues = this.lastDropzone.initialDragValues;
    const placement = this.lastDropzone.placement;
    const dropzone = this.lastDropzone.dropzone;
    const targetTag = this.lastDropzone.targetTag;
    const linkTag = this.lastDropzone.linkTag;
    const collapsedDropzoneContainer = this.lastDropzone.collapsedDropzoneContainer;
    this.showingDropzone = true;
    if (collapsedDropzoneContainer) {
      collapsedDropzoneContainer.classList.add('collapsed-dropzone');
    } else if (linkTag) {
      this.showLinkDropzone({ initialDragValues, dropzone, linkTag });
    } else if (targetTag) {
      this.showDropzone({ initialDragValues, placement, dropzone, targetTag });
      targetTag.topLevelParentTag().rediscoverLinkPositions();
    }
  }

  hideDropzone(dropzone) {
    if (this.linkDropzoneTipTimer) {
      clearTimeout(this.linkDropzoneTipTimer);
    }
    this.clearNewHome();
    // TODO: put this back and fix freehand dropzones never disappearing
    //if (!this.showingDropzone) return;
    const targetTag = this.lastDropzone.targetTag;
    this.showingDropzone = false;
    this.clearHighlightedTag();
    dropzone.style.display = 'none';
    if (dropzone.classList.contains('link-dropzone')) {
      dropzone.classList.remove('link-dropzone');
    }
    dropzone.removeAttribute('data-tip-align');
    dropzone.removeAttribute('data-tip');
    dropzone.classList.remove('forced-on');
    if (this.lastDropzone.collapsedDropzoneContainer) {
      this.lastDropzone.collapsedDropzoneContainer.classList.remove('collapsed-dropzone');
    }
    if (targetTag) {
      const tlpTag = targetTag.topLevelParentTag();
      tlpTag.rediscoverLinkPositions();
      if (tlpTag.linkTag()) {
        const box = tlpTag.linkTag().describeArc().outerBox;
        tlpTag.linkTag().setClipPolygon(box);
      }
    }
    if (this.lastDropZone && this.lastDrpozone.dragNodeTag) {
      this.lastDropzone.dragNodeTag.refs.container.style.opacity = null;
    }
  }

  clearHighlightedTag() {
    if (this.highlightedTag && this.highlightedTag.refs.container) {
      if (this.highlightedTag.childNodesTag && this.highlightedTag.childNodesTag.refs.container) {
        this.highlightedTag.childNodesTag.refs.container.classList.remove('top-hover');
      }
      this.highlightedTag.refs.container.classList.remove('top-hover');
      this.highlightedTag.refs.nodeTopLine.classList.remove('node-top-line-top-hover');
      this.highlightedTag = null;
    }
  }

  isShowingPopout(node) {
    const nodeTag = this.canvas.getNodeTag({id: node.id});
    return node.childrenDetached && !node.getIsCollapsed() && node.children().length && nodeTag && nodeTag.childNodesTag;
  }
}

// singleton instance
const dragdrop = new DragDrop();
window.dragdrop = dragdrop;
export { DragDrop, dragdrop };



// WEBPACK FOOTER //
// ./starling/src/lib/drag-drop.js